import React, {
    useReducer, useEffect, useCallback, useRef, useMemo,
} from 'react';

// Components and Others
import clsx from 'clsx';
import PropTypes from 'prop-types';
import Table from 'components/widgets/Table';
import { withRouter } from 'react-router-dom';
import { Checkbox } from '@material-ui/core';
import update from 'immutability-helper';

// Others
import ModalUtils from 'utils/ModalUtils';
import ContractService from 'services/modules/ContractService';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';

// Material UI
import { withStyles } from '@material-ui/core/styles';
import PortfolioSettingsStyles from 'styles/modules/settings/PortfolioSettingsStyles';
import Button from '@material-ui/core/Button';
import { CheckBox as CheckIcon, Replay, OpenInNew } from '@material-ui/icons';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import ContractDetailDialog from './ContractDetailDialog';
import contractToFormData from './contractUtils';

const styles = (theme) => PortfolioSettingsStyles.listStyles(theme);

const ACTION_TYPE = {
    SET_SELECTED_CONTRACT_ID: 'setSelectedContractId',
    SET_DIALOG_OPEN: 'setDialogOpen',
    SET_SELECTED_CONTRACT: 'setSelectedContract',
    SET_IS_CLONING: 'setIsCloning',
    SET_IS_EDITING: 'setIsEditing',
    SET_FORM_DATA: 'setFormData',
    SET_SHOW_CONFIRM_DIALOG: 'setShowConfirmDialog',
    SET_DEALS_COUNT: 'setDealsCount',
    SET_IS_LOADING_DEALS_COUNT: 'setIsLoadingDealsCount',
    SET_IS_SETTING_DEFAULT: 'setIsSettingDefault',
    SET_IS_RELOADING: 'setIsReloading',
    SET_TABLE_CONTENT: 'setTableContent',
    RESET_DIALOG_STATE: 'resetDialogState',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPE.SET_SELECTED_CONTRACT_ID:
        return update(state, {
            selectedContractId: { $set: action.value },
        });
    case ACTION_TYPE.SET_DIALOG_OPEN:
        return update(state, {
            isDialogOpen: { $set: action.value },
        });
    case ACTION_TYPE.SET_SELECTED_CONTRACT:
        return update(state, {
            selectedContract: { $set: action.value },
        });
    case ACTION_TYPE.SET_IS_CLONING:
        return update(state, {
            isCloning: { $set: action.value },
        });
    case ACTION_TYPE.SET_IS_EDITING:
        return update(state, {
            isEditing: { $set: action.value },
        });
    case ACTION_TYPE.SET_FORM_DATA:
        return update(state, {
            formData: { $set: action.value },
        });
    case ACTION_TYPE.SET_SHOW_CONFIRM_DIALOG:
        return update(state, {
            showConfirmDialog: { $set: action.value },
        });
    case ACTION_TYPE.SET_DEALS_COUNT:
        return update(state, {
            dealsCount: { $set: action.value },
        });
    case ACTION_TYPE.SET_IS_LOADING_DEALS_COUNT:
        return update(state, {
            isLoadingDealsCount: { $set: action.value },
        });
    case ACTION_TYPE.SET_IS_SETTING_DEFAULT:
        return update(state, {
            isSettingDefault: { $set: action.value },
        });
    case ACTION_TYPE.SET_IS_RELOADING:
        return update(state, {
            isReloading: { $set: action.value },
        });
    case ACTION_TYPE.SET_TABLE_CONTENT:
        return update(state, {
            tableContent: { $set: action.value },
        });
    case ACTION_TYPE.RESET_DIALOG_STATE:
        return update(state, {
            isDialogOpen: { $set: false },
            selectedContract: { $set: null },
            isEditing: { $set: false },
            formData: { $set: null },
            showConfirmDialog: { $set: false },
            dealsCount: { $set: 0 },
        });
    default:
        return state;
    }
};

const ContractSettingListComponent = ({ classes }) => {
    const keyStore = useMemo(() => new KeyStore(), []);
    const SETTINGS_BHPH_CONTRACT_EDIT = keyStore.hasPermission(Permission.SETTINGS_BHPH_CONTRACT_EDIT);

    const contractServiceRef = useRef(new ContractService());

    const [state, dispatch] = useReducer(reducer, {
        selectedContractId: null,
        isDialogOpen: false,
        selectedContract: null,
        isCloning: false,
        isEditing: false,
        formData: null,
        showConfirmDialog: false,
        dealsCount: 0,
        isLoadingDealsCount: false,
        isSettingDefault: false,
        isReloading: false,
        tableContent: {
            records: [],
            tableLength: 0,
            loading: true,
            limit: 50,
            sortable: true,
            desc: false,
            orderBy: 'loanContractTemplateId',
        },
    });

    const fetchContracts = useCallback(() => contractServiceRef.current.getContractsList()
        .then((response) => {
            const { data, graphQLErrors } = response;

            if (graphQLErrors) {
                ModalUtils.errorMessage(graphQLErrors);
                return;
            }

            if (data && data.contractList) {
                const { contractList, totalCount } = data;

                dispatch({
                    type: ACTION_TYPE.SET_TABLE_CONTENT,
                    value: {
                        records: contractList || [],
                        tableLength: totalCount || 0,
                        loading: false,
                        limit: 50,
                        sortable: true,
                        desc: false,
                        orderBy: 'loanContractTemplateId',
                    },
                });
            } else {
                dispatch({
                    type: ACTION_TYPE.SET_TABLE_CONTENT,
                    value: {
                        records: [],
                        tableLength: 0,
                        loading: false,
                        limit: 50,
                        sortable: true,
                        desc: false,
                        orderBy: 'loanContractTemplateId',
                    },
                });
            }
        })
        .catch((error) => {
            ModalUtils.errorMessage(error);
            dispatch({
                type: ACTION_TYPE.SET_TABLE_CONTENT,
                value: {
                    records: [],
                    tableLength: 0,
                    loading: false,
                    limit: 50,
                    sortable: true,
                    desc: false,
                    orderBy: 'loanContractTemplateId',
                },
            });
        }), []);

    useEffect(() => {
        fetchContracts();
    }, [fetchContracts]);

    const handleSetSelectedContractId = useCallback((contractId) => {
        dispatch({
            type: ACTION_TYPE.SET_SELECTED_CONTRACT_ID,
            value: contractId,
        });
    }, []);

    const onReload = useCallback(() => {
        dispatch({
            type: ACTION_TYPE.SET_SELECTED_CONTRACT_ID,
            value: null,
        });
        dispatch({
            type: ACTION_TYPE.SET_IS_RELOADING,
            value: true,
        });
        dispatch({
            type: ACTION_TYPE.SET_TABLE_CONTENT,
            value: {
                records: [],
                tableLength: 0,
                loading: true,
                limit: 50,
                sortable: true,
                desc: false,
                orderBy: 'loanContractTemplateId',
            },
        });

        fetchContracts()
            .finally(() => {
                dispatch({
                    type: ACTION_TYPE.SET_IS_RELOADING,
                    value: false,
                });
            });
    }, [fetchContracts]);

    const onOpen = useCallback(() => {
        if (!state.selectedContractId) {
            return;
        }

        const contract = state.tableContent.records.find(
            (record) => record.loanContractTemplateId === state.selectedContractId,
        );

        if (contract) {
            dispatch({
                type: ACTION_TYPE.SET_SELECTED_CONTRACT,
                value: contract,
            });
            dispatch({
                type: ACTION_TYPE.SET_DIALOG_OPEN,
                value: true,
            });
        }
    }, [state.selectedContractId, state.tableContent.records]);

    const handleCloseDialog = useCallback(() => {
        dispatch({
            type: ACTION_TYPE.RESET_DIALOG_STATE,
        });
    }, []);

    const handleClone = useCallback(async () => {
        const contractId = state.selectedContract?.loanContractTemplateId;

        if (!contractId || state.isCloning) {
            return;
        }

        dispatch({
            type: ACTION_TYPE.SET_IS_CLONING,
            value: true,
        });

        try {
            const response = await contractServiceRef.current.cloneLoanContractTemplate({
                loanContractTemplateId: contractId,
            });
            const { data, graphQLErrors } = response || {};

            if (graphQLErrors) {
                ModalUtils.errorMessage(graphQLErrors);
                return;
            }

            const clonedId = data?.cloneLoanContractTemplate?.loanContractTemplateId;

            if (!clonedId) {
                ModalUtils.errorMessage(null, 'Error cloning contract');
                return;
            }

            ModalUtils.successMessage(null, 'Contract cloned successfully');
            fetchContracts();
            handleCloseDialog();
        } catch (error) {
            ModalUtils.errorMessage(error);
        } finally {
            dispatch({
                type: ACTION_TYPE.SET_IS_CLONING,
                value: false,
            });
        }
    }, [state.selectedContract, state.isCloning, fetchContracts, handleCloseDialog]);

    const handleEdit = useCallback(() => {
        if (!state.selectedContract) {
            return;
        }

        dispatch({
            type: ACTION_TYPE.SET_FORM_DATA,
            value: contractToFormData(state.selectedContract),
        });
        dispatch({
            type: ACTION_TYPE.SET_IS_EDITING,
            value: true,
        });
    }, [state.selectedContract]);

    const handleEditFieldChange = useCallback((updatedFormData) => {
        dispatch({
            type: ACTION_TYPE.SET_FORM_DATA,
            value: updatedFormData,
        });
    }, []);

    const handleCancelEdit = useCallback(() => {
        dispatch({
            type: ACTION_TYPE.SET_IS_EDITING,
            value: false,
        });
        if (state.selectedContract) {
            dispatch({
                type: ACTION_TYPE.SET_FORM_DATA,
                value: contractToFormData(state.selectedContract),
            });
        }
    }, [state.selectedContract]);

    const handleSave = useCallback(async () => {
        const contractId = state.selectedContract?.loanContractTemplateId;
        if (!contractId) {
            return;
        }

        dispatch({
            type: ACTION_TYPE.SET_IS_LOADING_DEALS_COUNT,
            value: true,
        });

        try {
            const response = await contractServiceRef.current.getDealsCountByLoanContractId({
                loanContractId: contractId,
            });

            const { data, graphQLErrors } = response || {};

            if (graphQLErrors) {
                ModalUtils.errorMessage(graphQLErrors);
                dispatch({
                    type: ACTION_TYPE.SET_IS_LOADING_DEALS_COUNT,
                    value: false,
                });
                return;
            }

            const count = data?.deals || 0;
            dispatch({
                type: ACTION_TYPE.SET_DEALS_COUNT,
                value: count,
            });
            dispatch({
                type: ACTION_TYPE.SET_SHOW_CONFIRM_DIALOG,
                value: true,
            });
        } catch (error) {
            ModalUtils.errorMessage(error);
        } finally {
            dispatch({
                type: ACTION_TYPE.SET_IS_LOADING_DEALS_COUNT,
                value: false,
            });
        }
    }, [state.selectedContract]);

    const handleConfirmSave = useCallback(async () => {
        const contractId = state.selectedContract?.loanContractTemplateId;
        if (!contractId || !state.formData) {
            return;
        }

        dispatch({
            type: ACTION_TYPE.SET_SHOW_CONFIRM_DIALOG,
            value: false,
        });
        dispatch({
            type: ACTION_TYPE.SET_IS_LOADING_DEALS_COUNT,
            value: true,
        });

        try {
            const input = {
                name: state.formData.name || '',
                allowLateFees: state.formData.allowLateFees || false,
                allowNSFCharges: state.formData.allowNSFCharges || false,
                lateFeeAllocationLogic: state.formData.lateFeeAllocationLogic || '',
                lateFeeDays: state.formData.lateFeeDays || 0,
                lateFeeMax: state.formData.lateFeeMax || 0,
                lateFeeMin: state.formData.lateFeeMin || 0,
                lateFeePercentage: state.formData.lateFeePercentage || 0,
                lateFeeCalculatedBy: state.formData.lateFeeCalculatedBy || 'Payment',
                nSFCharges: state.formData.nSFCharges || 0,
            };

            const response = await contractServiceRef.current.updateLoanContractTemplate({
                loanContractTemplateId: contractId,
                input,
            });

            const { data, graphQLErrors } = response || {};

            if (graphQLErrors) {
                ModalUtils.errorMessage(graphQLErrors);
                dispatch({
                    type: ACTION_TYPE.SET_IS_LOADING_DEALS_COUNT,
                    value: false,
                });
                return;
            }

            if (data && data.updateLoanContractTemplate) {
                ModalUtils.successMessage(null, 'Contract updated successfully');
                dispatch({
                    type: ACTION_TYPE.SET_IS_EDITING,
                    value: false,
                });
                fetchContracts();
                handleCloseDialog();
            } else {
                ModalUtils.errorMessage(null, 'Error updating contract');
            }
        } catch (error) {
            ModalUtils.errorMessage(error);
        } finally {
            dispatch({
                type: ACTION_TYPE.SET_IS_LOADING_DEALS_COUNT,
                value: false,
            });
        }
    }, [state.selectedContract, state.formData, fetchContracts, handleCloseDialog]);

    const handleCancelConfirm = useCallback(() => {
        dispatch({
            type: ACTION_TYPE.SET_SHOW_CONFIRM_DIALOG,
            value: false,
        });
    }, []);

    const onDefault = useCallback(() => {
        if (!state.selectedContractId || state.isSettingDefault) {
            return;
        }

        dispatch({
            type: ACTION_TYPE.SET_IS_SETTING_DEFAULT,
            value: true,
        });

        contractServiceRef.current.setDefaultLoanContractTemplate({ id: state.selectedContractId })
            .then((response) => {
                const { data, graphQLErrors } = response;

                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }

                if (data && data.setDefaultLoanContractTemplate) {
                    ModalUtils.successMessage(null, 'Contract was set as default successfully');
                    fetchContracts();
                }
            })
            .catch((error) => {
                ModalUtils.errorMessage(error);
            })
            .finally(() => {
                dispatch({
                    type: ACTION_TYPE.SET_IS_SETTING_DEFAULT,
                    value: false,
                });
            });
    }, [state.selectedContractId, state.isSettingDefault, fetchContracts]);

    const getColumns = useMemo(() => [
        {
            Header: 'Contract Code',
            accessor: 'loanContractTemplateId',
            className: classes.columnStyle,
            minWidth: 120,
            width: 120,
        },
        {
            Header: 'Name',
            accessor: 'name',
            className: classes.columnStyle,
        },
        {
            Header: 'Default',
            accessor: 'isDefault',
            style: { textAlign: 'center' },
            // eslint-disable-next-line react/prop-types
            Cell: ({ value }) => {
                const isDefault = value;
                return isDefault ? (
                    <div>
                        <Checkbox
                            checked={isDefault}
                            name="completed"
                            color="primary"
                        />
                    </div>
                ) : null;
            },
        },
    ], [classes.columnStyle]);

    const disableEditAndSetDefault = state.selectedContractId === null;

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Button
                    size="small"
                    variant="contained"
                    className={clsx(classes.button, classes.editButton)}
                    startIcon={<OpenInNew />}
                    disabled={disableEditAndSetDefault}
                    onClick={onOpen}
                >
                    Open
                </Button>
                {SETTINGS_BHPH_CONTRACT_EDIT && (
                    <Button
                        size="small"
                        variant="contained"
                        className={clsx(classes.button, classes.setDefaultButton)}
                        startIcon={<CheckIcon />}
                        disabled={disableEditAndSetDefault || state.isSettingDefault}
                        onClick={onDefault}
                    >
                        Set Default
                    </Button>
                )}
                <Button
                    size="small"
                    variant="contained"
                    className={clsx(classes.button, classes.editButton)}
                    startIcon={<Replay />}
                    disabled={state.isReloading}
                    onClick={onReload}
                >
                    Reload
                </Button>
            </div>
            <div className={classes.tableHeight}>
                <Table
                    rowSelected
                    load={state.tableContent.loading}
                    totalRecords={state.tableContent.records.length}
                    sortable={state.tableContent.sortable}
                    data={state.tableContent.records}
                    columns={getColumns}
                    unselectRow={disableEditAndSetDefault}
                    getTrProps={(_, rowInfo) => {
                        const record = rowInfo.original;
                        const isSelected = state.selectedContractId === record.loanContractTemplateId;

                        return {
                            onClick: (event) => {
                                event.currentTarget.classList.add('active');
                                handleSetSelectedContractId(record.loanContractTemplateId);
                            },
                            onDoubleClick: (event) => {
                                event.currentTarget.classList.add('active');
                                handleSetSelectedContractId(record.loanContractTemplateId);
                                dispatch({
                                    type: ACTION_TYPE.SET_SELECTED_CONTRACT,
                                    value: record,
                                });
                                dispatch({
                                    type: ACTION_TYPE.SET_DIALOG_OPEN,
                                    value: true,
                                });
                            },
                            className: isSelected ? 'active' : '',
                        };
                    }}
                />
            </div>
            <ContractDetailDialog
                open={state.isDialogOpen}
                onClose={handleCloseDialog}
                contract={state.selectedContract}
                onClone={handleClone}
                onEdit={handleEdit}
                onEditFieldChange={handleEditFieldChange}
                onSave={handleSave}
                onCancel={handleCancelEdit}
                formData={state.formData}
                isEditing={state.isEditing}
                isLoadingDealsCount={state.isLoadingDealsCount}
                isCloning={state.isCloning}
            />
            <ConfirmDialog
                title="Confirm Update"
                description={
                    `This update will affect future payment and fee rules for ${state.dealsCount} deal(s) `
                    + 'currently associated with this contract. Do you want to continue?'
                }
                open={state.showConfirmDialog}
                showHeader
                titlePrimary="Continue"
                titleSecondary="Cancel"
                variant="outlined"
                onClickPrimary={handleConfirmSave}
                onClickSecondary={handleCancelConfirm}
                onClose={handleCancelConfirm}
                loading={state.isLoadingDealsCount}
                disablePrimaryButton={state.isLoadingDealsCount}
                disableSecondaryButton={state.isLoadingDealsCount}
            />
        </div>
    );
};

ContractSettingListComponent.propTypes = {
    classes: PropTypes.shape({
        root: PropTypes.string.isRequired,
        header: PropTypes.string.isRequired,
        button: PropTypes.string.isRequired,
        setDefaultButton: PropTypes.string.isRequired,
        editButton: PropTypes.string.isRequired,
        tableHeight: PropTypes.string.isRequired,
        columnStyle: PropTypes.string.isRequired,
    }).isRequired,
};

export default withRouter(withStyles(styles)(ContractSettingListComponent));
